import { useState } from 'react';
import { FiX, FiTwitter, FiFacebook, FiLink, FiCheck } from 'react-icons/fi';
import { Highlight } from '../../types';

interface ShareModalProps {
  onClose: () => void;
  highlight?: Highlight;
  bookTitle?: string;
  bookAuthor?: string;
  customText?: string;
}

const ShareModal = ({ onClose, highlight, bookTitle, bookAuthor, customText }: ShareModalProps) => {
  const [copied, setCopied] = useState(false);

  // 공유할 텍스트 생성
  const shareText = customText || (highlight ?
    `"${highlight.text}"\n\n— ${bookTitle} by ${bookAuthor}\n\n#독서 #WIABooks` :
    `${bookTitle} by ${bookAuthor}를 읽고 있어요! 📚\n\n#독서 #WIABooks`
  );

  const encodedText = encodeURIComponent(shareText);
  const currentUrl = window.location.href;

  // Twitter 공유
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodeURIComponent(currentUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  // Facebook 공유
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}&quote=${encodedText}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  // 링크 복사
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareText + '\n\n' + currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('클립보드 복사에 실패했습니다.');
    }
  };

  // Web Share API (모바일)
  const shareNative = async () => {
    if ('share' in navigator) {
      try {
        await navigator.share({
          title: bookTitle || 'WIA Book',
          text: shareText,
          url: currentUrl,
        });
      } catch (err) {
        console.log('공유 취소 또는 실패:', err);
      }
    }
  };

  // Web Share API 지원 여부
  const supportsShare = 'share' in navigator;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            📤 공유하기
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* 공유할 내용 미리보기 */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {shareText}
          </p>
        </div>

        {/* 공유 버튼들 */}
        <div className="space-y-3 mb-6">
          {/* Twitter */}
          <button
            onClick={shareToTwitter}
            className="w-full flex items-center gap-3 px-4 py-3 bg-[#1DA1F2] text-white rounded-lg hover:bg-[#1a8cd8] transition-colors"
          >
            <FiTwitter size={20} />
            <span className="font-medium">Twitter에 공유</span>
          </button>

          {/* Facebook */}
          <button
            onClick={shareToFacebook}
            className="w-full flex items-center gap-3 px-4 py-3 bg-[#1877F2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
          >
            <FiFacebook size={20} />
            <span className="font-medium">Facebook에 공유</span>
          </button>

          {/* 링크 복사 */}
          <button
            onClick={copyToClipboard}
            className="w-full flex items-center gap-3 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            {copied ? (
              <>
                <FiCheck size={20} className="text-green-500" />
                <span className="font-medium text-green-500">복사 완료!</span>
              </>
            ) : (
              <>
                <FiLink size={20} />
                <span className="font-medium">링크 복사</span>
              </>
            )}
          </button>

          {/* 모바일 네이티브 공유 */}
          {supportsShare && (
            <button
              onClick={shareNative}
              className="w-full flex items-center gap-3 px-4 py-3 bg-primary text-white rounded-lg hover:shadow-lg transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              <span className="font-medium">더 많은 방법으로 공유</span>
            </button>
          )}
        </div>

        {/* 닫기 버튼 */}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          닫기
        </button>
      </div>
    </div>
  );
};

export default ShareModal;
